import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Box } from "@mui/material";

import { ActionButton, FormDialog, InputField, SelectField } from "../v3";
import { AccountService, PerformanceGuaranteesService, ProjectService } from "../../services";
import { addPGValidation } from "../../pages/performanceGuarantees/validation";
import { formatPayload, useHasPermission, validateDateRange } from "../../utils";
import { VIEW_ACCOUNT } from "../../constants";

export const AddPerformanceGuarantees = ({
  open = false,
  projectId = null,
  pgData = null,
  onClose = () => {},
  onSuccess = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [pgDetails, setPgDetails] = useState({
    name: undefined,
    depositAmount: undefined,
    projectId: undefined,
    fromAccountId: undefined,
    depositDate: undefined,
    endDate: undefined,
    description: undefined,
  });

  const hasViewAccount = useHasPermission(VIEW_ACCOUNT);
  const { user } = useSelector((state) => state.user);
  const { ownerUserId: companyAdminId } = user.company;

  useEffect(() => {
    if (open) {
      if (pgData) {
        setPgDetails({
          name: pgData.name || "",
          depositAmount: pgData.depositAmount || "",
          projectId: pgData.projectId || null,
          fromAccountId: pgData.depositTransaction?.fromAccountId || null,
          depositDate: pgData.depositDate ? moment(pgData.depositDate).format("YYYY-MM-DD") : "",
          endDate: pgData.endDate ? moment(pgData.endDate).format("YYYY-MM-DD") : "",
          description: pgData.description || "",
        });
        if (pgData.projectId) fetchProjectData(pgData.projectId);
        if (pgData.depositTransaction?.fromAccountId) fetchAccountData(pgData.depositTransaction.fromAccountId);
      } else if (projectId) {
        fetchProjectData(projectId);
      }
    } else {
      setErrors({});
      setPgDetails({
        name: undefined,
        depositAmount: undefined,
        projectId: undefined,
        fromAccountId: undefined,
        depositDate: undefined,
        endDate: undefined,
        description: undefined,
      });
      setSelectedProject(null);
      setSelectedAccount(null);
    }
  }, [open, projectId, pgData]);

  const fetchProjectData = async (projId) => {
    try {
      const { data: response } = await ProjectService.getProjectById(projId);
      setSelectedProject(response);
      setPgDetails((prev) => ({ ...prev, projectId: response?.id }));
    } catch (error) {
      console.error("🚀 ~ fetchProjectData ~ error:", error);
    }
  };

  const fetchAccountData = async (accId) => {
    try {
      const { data: response } = await AccountService.getAccountById(accId);
      setSelectedAccount(response);
      setPgDetails((prev) => ({ ...prev, fromAccountId: response?.id }));
    } catch (error) {
      console.error("🚀 ~ fetchAccountData ~ error:", error);
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;

    if (name === "depositAmount") {
      setPgDetails((prev) => ({ ...prev, depositAmount: value }));

      const num = parseFloat(value);
      if (value !== "" && (isNaN(num) || num <= 0))
        setErrors((prev) => ({ ...prev, depositAmount: "Deposit amount must be greater than 0" }));
      else setErrors((prev) => ({ ...prev, depositAmount: null }));
    } else if (name === "depositDate" || name === "endDate") {
      setPgDetails((prev) => ({ ...prev, [name]: value }));

      const updated = {
        ...pgDetails,
        [name]: value,
      };

      const dateErrors = validateDateRange({
        fieldName: name,
        startDateKey: "depositDate",
        endDateKey: "endDate",
        startDate: updated.depositDate,
        endDate: updated.endDate,
        startErrorText: "Deposit date cannot be on or after end date",
        endErrorText: "End date cannot be on or before deposit date",
        allowSameDay: false,
      });

      setErrors((prev) => ({
        ...prev,
        depositDate: dateErrors.depositDate,
        endDate: dateErrors.endDate,
      }));
    } else {
      setPgDetails((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleProjectChange = (selected) => {
    setSelectedProject(selected);
    setPgDetails((prev) => ({ ...prev, projectId: selected?.id }));
  };

  const handleAccountChange = (selected) => {
    setSelectedAccount(selected);
    setPgDetails((prev) => ({ ...prev, fromAccountId: selected?.id || null }));
  };

  const handleCloseModal = () => {
    setErrors({});
    setPgDetails({
      name: undefined,
      depositAmount: undefined,
      projectId: undefined,
      fromAccountId: undefined,
      depositDate: undefined,
      endDate: undefined,
      description: undefined,
    });
    setSelectedProject(null);
    setSelectedAccount(null);
    onClose();
  };

  const handleSave = async () => {
    setErrors({});
    const formattedData = formatPayload(pgDetails);

    const validationErrors = addPGValidation(formattedData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formattedData,
        depositAmount: Number(formattedData.depositAmount),
        depositDate: new Date(formattedData.depositDate).toISOString(),
        endDate: moment(formattedData.endDate).toISOString(),
      };

      if (pgData?.id) {
        delete payload.projectId;
        await PerformanceGuaranteesService.updatePerformanceGuarantee(pgData.id, payload);
        toast.success("Performance Guarantee updated successfully");
      } else {
        await PerformanceGuaranteesService.createPerformanceGuarantee(payload);
        toast.success("Performance Guarantee added successfully");
      }
      onSuccess?.();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving Performance Guarantee:", error);
      toast.error(`Failed to ${pgData?.id ? "update" : "add"} Performance Guarantee. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={handleCloseModal}
      title={pgData?.id ? "Edit PG & AD PG" : "Add PG & AD PG"}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSave} disabled={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Name"
            name="name"
            value={pgDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
          <InputField
            required
            label="Amount"
            name="depositAmount"
            value={pgDetails.depositAmount}
            onChange={updateDetailsState}
            error={!!errors.depositAmount}
            helperText={errors.depositAmount}
            fullWidth
          />
        </Box>
        <Box display="flex" gap={2}>
          <SelectField
            label="Project"
            value={selectedProject}
            getData={ProjectService.getProjectList}
            getDataStaticFilters={{ status: ["TODO", "INPROGRESS"], isGovernment: true }}
            required
            disabled={!!projectId || !!pgData?.id}
            error={!!errors.projectId}
            helperText={errors.projectId}
            onChange={handleProjectChange}
            style={{ width: "100%" }}
          />
          <SelectField
            label="Account"
            value={selectedAccount}
            getData={AccountService.getAdminAndUserAccounts}
            getDataStaticFilters={{ hasViewAccount, user, companyAdminId }}
            required
            error={!!errors.fromAccountId}
            helperText={errors.fromAccountId}
            onChange={handleAccountChange}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            required
            type="date"
            label="Deposit Date"
            name="depositDate"
            value={pgDetails?.depositDate}
            onChange={updateDetailsState}
            error={!!errors.depositDate}
            helperText={errors.depositDate}
            style={{ width: "100%" }}
          />
          <InputField
            required
            type="date"
            label="End Date"
            name="endDate"
            value={pgDetails?.endDate}
            onChange={updateDetailsState}
            error={!!errors.endDate}
            helperText={errors.endDate}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            label="Description"
            name="description"
            value={pgDetails.description}
            onChange={updateDetailsState}
            error={!!errors.description}
            helperText={errors.description}
            fullWidth
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
